import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search,
  UserCog,
  Clock,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Users,
  Target,
} from "lucide-react";

// Mock staff data
const MOCK_STAFF = [
  {
    id: "1",
    name: "John Analyst",
    role: "Senior Analyst",
    dealsAssigned: 8,
    dealsCompleted: 5,
    avgCompletionTime: 4.2,
    activeHours: 38,
    status: "active",
  },
  {
    id: "2",
    name: "Jane Analyst",
    role: "Analyst",
    dealsAssigned: 6,
    dealsCompleted: 4,
    avgCompletionTime: 5.1,
    activeHours: 42,
    status: "active",
  },
  {
    id: "3",
    name: "Mike Review",
    role: "CIM Reviewer",
    dealsAssigned: 12,
    dealsCompleted: 10,
    avgCompletionTime: 2.8,
    activeHours: 45,
    status: "busy",
  },
];

// Mock tasks data
const MOCK_TASKS = [
  { id: "1", title: "Review CIM for Tech Startup", assignee: "John Analyst", priority: "high", due: new Date(), status: "in_progress" },
  { id: "2", title: "Evaluate Restaurant Chain", assignee: "Jane Analyst", priority: "medium", due: new Date(Date.now() + 86400000), status: "pending" },
  { id: "3", title: "Complete Due Diligence", assignee: "Mike Review", priority: "high", due: new Date(Date.now() - 86400000), status: "overdue" },
];

export default function StaffProductivity() {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const totalDeals = MOCK_STAFF.reduce((sum, s) => sum + s.dealsAssigned, 0);
  const completedDeals = MOCK_STAFF.reduce((sum, s) => sum + s.dealsCompleted, 0);
  const avgCompletion = MOCK_STAFF.reduce((sum, s) => sum + s.avgCompletionTime, 0) / MOCK_STAFF.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Staff & Productivity</h1>
          <p className="text-white/60 mt-1">
            Monitor team performance and workload distribution
          </p>
        </div>
        <Button className="bg-brand-gold text-brand-navy hover:bg-brand-gold/90">
          <UserCog className="h-4 w-4 mr-2" />
          Manage Roles
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <Users className="h-6 w-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Active Staff</p>
                <p className="text-2xl font-bold text-white">{MOCK_STAFF.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-500/20">
                <BarChart3 className="h-6 w-6 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Total Deals</p>
                <p className="text-2xl font-bold text-white">{totalDeals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/20">
                <Target className="h-6 w-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Completion Rate</p>
                <p className="text-2xl font-bold text-white">
                  {((completedDeals / totalDeals) * 100).toFixed(0)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <Clock className="h-6 w-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-white/60">Avg. Completion</p>
                <p className="text-2xl font-bold text-white">{avgCompletion.toFixed(1)} days</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white/10">
          <TabsTrigger value="overview" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
            Overview
          </TabsTrigger>
          <TabsTrigger value="tasks" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
            Tasks
          </TabsTrigger>
          <TabsTrigger value="pipeline" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
            Pipeline
          </TabsTrigger>
          <TabsTrigger value="load" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">
            Load Distribution
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Staff Cards */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {MOCK_STAFF.map((staff) => (
            <Card key={staff.id} className="bg-white/10 border-white/20">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-brand-gold/20 flex items-center justify-center">
                      <span className="text-lg font-semibold text-brand-gold">
                        {staff.name[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{staff.name}</h3>
                      <p className="text-sm text-white/60">{staff.role}</p>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className={
                      staff.status === "active"
                        ? "bg-green-500/20 text-green-400 border-green-500/50"
                        : "bg-yellow-500/20 text-yellow-400 border-yellow-500/50"
                    }
                  >
                    {staff.status}
                  </Badge>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/60">Deals Progress</span>
                      <span className="text-white">
                        {staff.dealsCompleted}/{staff.dealsAssigned}
                      </span>
                    </div>
                    <Progress
                      value={(staff.dealsCompleted / staff.dealsAssigned) * 100}
                      className="h-2"
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Avg. Completion</span>
                    <span className="text-white">{staff.avgCompletionTime} days</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/60">Active Hours</span>
                    <span className="text-white">{staff.activeHours}h/week</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Tasks View */}
      {activeTab === "tasks" && (
        <Card className="bg-white/10 border-white/20">
          <CardHeader>
            <CardTitle className="text-white">Active Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {MOCK_TASKS.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-2 rounded ${
                        task.status === "overdue"
                          ? "bg-red-500/20"
                          : task.status === "in_progress"
                          ? "bg-blue-500/20"
                          : "bg-yellow-500/20"
                      }`}
                    >
                      {task.status === "overdue" ? (
                        <AlertCircle className="h-5 w-5 text-red-400" />
                      ) : task.status === "in_progress" ? (
                        <Clock className="h-5 w-5 text-blue-400" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-yellow-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-white">{task.title}</p>
                      <p className="text-sm text-white/60">Assigned to: {task.assignee}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Badge
                      variant="outline"
                      className={
                        task.priority === "high"
                          ? "text-red-400 border-red-400/50"
                          : "text-yellow-400 border-yellow-400/50"
                      }
                    >
                      {task.priority}
                    </Badge>
                    <span className="text-sm text-white/60">
                      Due: {task.due.toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
